/* @src/pre-deployment/src/components/ComponentTestBench.js v1.1 */
/* Added info modal with usage instructions and component registration guide */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../../contexts/AuthContext';
import { getComponentRegistry } from '../../utils/componentScanner';

import '../../styles/ComponentTestBench.css';

const ComponentTestBench = () => {
    const { isAdmin, user } = useAuth();
    const navigate = useNavigate();
    const [registry, setRegistry] = useState({});
    const [selectedModule, setSelectedModule] = useState('');
    const [level, setLevel] = useState('1');
    const [chapter, setChapter] = useState('1');
    const [contentId, setContentId] = useState('');
    const [showInstructions, setShowInstructions] = useState(false);

    useEffect(() => {
        // Load registry
        const components = getComponentRegistry();
        setRegistry(components);

        // Set default if exists
        const keys = Object.keys(components);
        if (keys.length > 0) {
            setSelectedModule(keys[0]);
        }
    }, []);

    if (!isAdmin) {
        return (
            <div className="test-bench-container">
                <div className="test-bench-card" style={{ textAlign: 'center' }}>
                    <h1>Access Denied</h1>
                    <p>You must be an administrator to access the Component Test Bench.</p>
                    <button className="start-test-btn" onClick={() => navigate('/app-homepage')}>Return Home</button>
                </div>
            </div>
        );
    }

    const handleStart = () => {
        if (!selectedModule) return;

        // Special handling for AdvertTeachModule
        if (selectedModule === 'advertTeach') {
            if (contentId) {
                // Navigate to preview page with adId
                navigate(`/admin/marketing/preview/${contentId}`);
            } else {
                alert('Please enter an AdvertTeach module ID (adId) to preview.');
            }
            return;
        }

        // Component name from registry mapping
        const componentName = registry[selectedModule].component;
        let url = `/test-module/${componentName}/${level}/${chapter}`;
        const params = [];
        if (contentId) params.push(`contentId=${encodeURIComponent(contentId)}`);
        // DialogueUniversal needs moduleName (e.g. dialogueVocab) to resolve Firestore paths; pass selectedModule
        if (componentName === 'DialogueUniversal') {
            params.push(`moduleKey=${encodeURIComponent(selectedModule)}`);
        }
        if (params.length) url += '?' + params.join('&');
        navigate(url);
    };

    return (
        <div className="test-bench-container">
            <div className="test-bench-card">
                <div className="test-bench-header">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1rem' }}>
                        <h1>Module Test Bench</h1>
                        <button
                            className="info-btn"
                            onClick={() => setShowInstructions(true)}
                            title="How to use the Test Bench"
                        >
                            <i className="fas fa-info-circle"></i>
                        </button>
                    </div>
                    <p>Developer suite for independent component testing and auditing</p>
                </div>

                <div className="test-bench-form">
                    <div className="form-group">
                        <label>Target Module</label>
                        <select
                            className="test-bench-select"
                            value={selectedModule}
                            onChange={(e) => setSelectedModule(e.target.value)}
                        >
                            {Object.entries(registry).map(([id, config]) => (
                                <option key={id} value={id}>
                                    {config.displayName} ({config.component})
                                </option>
                            ))}
                        </select>
                    </div>

                    <div style={{ display: 'flex', gap: '20px' }}>
                        <div className="form-group" style={{ flex: 1 }}>
                            <label>Level</label>
                            <select
                                className="test-bench-select"
                                value={level}
                                onChange={(e) => setLevel(e.target.value)}
                            >
                                {[0, 1, 2, 3, 4, 5].map(l => (
                                    <option key={l} value={l}>Level {l}</option>
                                ))}
                            </select>
                        </div>

                        <div className="form-group" style={{ flex: 1 }}>
                            <label>Chapter</label>
                            <select
                                className="test-bench-select"
                                value={chapter}
                                onChange={(e) => setChapter(e.target.value)}
                            >
                                {Array.from({ length: 30 }, (_, i) => i + 1).map(c => (
                                    <option key={c} value={c}>Chapter {c}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="form-group">
                        <label>Content ID (Optional)</label>
                        <input
                            type="text"
                            className="test-bench-select"
                            placeholder={selectedModule === 'advertTeach' ? 'e.g. ad_1234567890_abc123' : 'e.g. ch2_gmr_teach_001'}
                            value={contentId}
                            onChange={(e) => setContentId(e.target.value)}
                            style={{ width: '100%', padding: '0.75rem', border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.05)', color: 'white', borderRadius: '8px' }}
                        />
                        <small style={{ color: '#aaa', marginTop: '5px', display: 'block' }}>
                            {selectedModule === 'advertTeach' 
                                ? 'Enter AdvertTeach module ID (e.g., ad_1234567890_abc123) from Firestore'
                                : 'Leave blank to use default/mock data.'}
                        </small>
                    </div>

                    <button className="start-test-btn" onClick={handleStart}>
                        <i className="fas fa-play-circle"></i>
                        Initialize Component Environment
                    </button>
                </div>

                <div className="admin-actions-section" style={{ marginTop: '2rem', padding: '1rem', borderTop: '1px solid #ccc' }}>
                    <h3><i className="fas fa-database"></i> Admin Data Management</h3>
                    <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
                        <button
                            className="test-bench-btn"
                            style={{ backgroundColor: '#10B981', color: 'white', padding: '0.5rem 1rem', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                            onClick={async () => {
                                if (window.confirm('Upload Chapter Location Data to Firestore? By confirming you are overwriting existing data.')) {
                                    try {
                                        // Dynamic import to avoid issues if file doesn't exist yet during build
                                        const { uploadAndVerifyChapterLocations } = await import('../../utils/uploadChapterLocations');
                                        const result = await uploadAndVerifyChapterLocations();
                                        if (result.success) {
                                            alert('✅ Upload Successful!');
                                        } else {
                                            alert('❌ Upload Failed: ' + result.error);
                                        }
                                    } catch (e) {
                                        alert('❌ Error: ' + e.message);
                                    }
                                }
                            }}
                        >
                            <i className="fas fa-cloud-upload-alt"></i> Upload Chapter Locations
                        </button>

                        <button
                            className="test-bench-btn"
                            style={{ backgroundColor: '#6a5af9', color: 'white', padding: '0.5rem 1rem', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                            onClick={() => {
                                alert(`To generate Vocab Teach content for Level ${level} Chapter ${chapter}, please run the following command in your terminal:\n\npython src/content_utilities/vocab_teach_generator.py --level ${level} --chapter ${chapter}`);
                            }}
                        >
                            <i className="fas fa-magic"></i> Generate Vocab Content
                        </button>
                    </div>
                </div>

                <div className="test-bench-footer" style={{ textAlign: 'center', marginTop: '40px' }}>
                    <p>Logged in as: <strong>{user?.email}</strong> (Admin)</p>
                    <p style={{ opacity: 0.5 }}>System identifies components from <code>componentScanner.js</code></p>
                </div>
            </div>

            {/* Instructions Modal */}
            {showInstructions && (
                <div className="instructions-overlay" onClick={() => setShowInstructions(false)}>
                    <div className="instructions-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="instructions-header">
                            <h2><i className="fas fa-book"></i> Test Bench Instructions</h2>
                            <button className="close-btn" onClick={() => setShowInstructions(false)}>
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                        <div className="instructions-content">
                            {/* SECTION 1: How to Use */}
                            <section className="instruction-section">
                                <h3><i className="fas fa-play-circle"></i> How to Use the Test Bench</h3>
                                <ol>
                                    <li>
                                        <strong>Select a Module:</strong> Choose the component you want to test from the "Target Module" dropdown.
                                        Each option shows the display name and the actual component name in parentheses.
                                    </li>
                                    <li>
                                        <strong>Set Level & Chapter:</strong> Configure the Level (0-5) and Chapter (1-30) context.
                                        These values are passed to the component as props and used to fetch appropriate data.
                                    </li>
                                    <li>
                                        <strong>Click "Initialize Component Environment":</strong> This navigates to
                                        <code>/test-module/[ComponentName]/[Level]/[Chapter]</code> where your component loads in isolation.
                                    </li>
                                    <li>
                                        <strong>Use the Test View Controls:</strong>
                                        <ul>
                                            <li><strong>Log:</strong> Shows runtime execution logs</li>
                                            <li><strong>Data:</strong> Shows Firestore data fetches</li>
                                            <li><strong>Tracking:</strong> Shows tracking event stream</li>
                                            <li><strong>Restart:</strong> Remounts the component fresh</li>
                                            <li><strong>Auto Test:</strong> Runs automated test sequence</li>
                                        </ul>
                                    </li>
                                </ol>
                            </section>

                            {/* SECTION 2: How to Register a Component */}
                            <section className="instruction-section">
                                <h3><i className="fas fa-plus-circle"></i> How to Register a New Component</h3>
                                <p>To add a new component to the Test Bench, you must update <strong>TWO files</strong>:</p>

                                <div className="code-section">
                                    <h4>Step 1: Add to componentScanner.js</h4>
                                    <p>File: <code>src/pre-deployment/src/utils/componentScanner.js</code></p>
                                    <p>Add your component to the <code>AVAILABLE_COMPONENTS_REGISTRY</code> object:</p>
                                    <pre>{`'myNewModule': {
  displayName: 'My New Module',    // Shown in dropdown
  routePath: 'myNewModule',        // URL path segment
  component: 'MyNewComponent',     // Exact React component name
  discovered: true,
  discoveredAt: '2025-01-02T00:00:00.000Z'
},`}</pre>
                                </div>

                                <div className="code-section">
                                    <h4>Step 2: Add to componentRegistry.js</h4>
                                    <p>File: <code>src/pre-deployment/src/utils/componentRegistry.js</code></p>
                                    <p>Add a lazy import to the <code>StaticComponentRegistry</code> object:</p>
                                    <pre>{`const StaticComponentRegistry = {
  // ... existing components ...
  MyNewComponent: React.lazy(() => 
    import('../components/MyNewComponent')
  ),
};`}</pre>
                                </div>

                                <div className="warning-box">
                                    <i className="fas fa-exclamation-triangle"></i>
                                    <div>
                                        <strong>Important:</strong> The <code>component</code> value in componentScanner.js
                                        must <strong>exactly match</strong> the key name in componentRegistry.js.
                                        Case-sensitive!
                                    </div>
                                </div>
                            </section>

                            {/* SECTION 3: Component Requirements */}
                            <section className="instruction-section">
                                <h3><i className="fas fa-check-circle"></i> Component Requirements</h3>
                                <p>Your component should accept these standard props:</p>
                                <pre>{`const MyNewComponent = ({
  level,           // String: "1", "2", etc.
  chapter,         // String: "1", "2", etc.
  moduleKey,       // String: unique key for this instance
  moduleConfig,    // Object: { displayName: "..." }
  navigation,      // Object: { next, previous, menu }
  isTestEnvironment, // Boolean: true when in test bench
  data,            // Object: optional data from parent
  onComplete,      // Function: call when module completes
}) => { ... }`}</pre>
                                <p>At minimum, your component should:</p>
                                <ul>
                                    <li>Have a default export</li>
                                    <li>Handle missing data gracefully</li>
                                    <li>Not crash on mount (use try/catch in useEffect)</li>
                                </ul>
                            </section>

                            {/* SECTION 4: Troubleshooting */}
                            <section className="instruction-section">
                                <h3><i className="fas fa-bug"></i> Troubleshooting</h3>
                                <table className="troubleshooting-table">
                                    <thead>
                                        <tr><th>Error</th><th>Cause</th><th>Fix</th></tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td>"Component Not Found"</td>
                                            <td>Component not in registry</td>
                                            <td>Add to both componentScanner.js AND componentRegistry.js</td>
                                        </tr>
                                        <tr>
                                            <td>"No component name provided"</td>
                                            <td>URL parameter mismatch</td>
                                            <td>Check route params match ComponentTestView.js</td>
                                        </tr>
                                        <tr>
                                            <td>React Error #306</td>
                                            <td>Invalid export or import path</td>
                                            <td>Verify component has default export and correct path</td>
                                        </tr>
                                        <tr>
                                            <td>Blank screen</td>
                                            <td>Component crashed silently</td>
                                            <td>Check browser console for errors, add error boundary</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </section>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ComponentTestBench;
